import { DollarSign, Phone, Calculator, TrendingUp, FileText, AlertCircle } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const ChildSupport = () => {
  return (
    <GenericPracticePage
      title="Child Support Attorney"
      subtitle="Establishing, modifying, and enforcing child support orders in Texas. Ensuring your children receive the financial support they deserve."
      icon={<DollarSign className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Texas Child Support Lawyer</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Child support is not optional in Texas - it's the legal obligation of both parents to financially support their children. Whether you need to establish support, modify an existing order, or enforce unpaid support, we provide experienced representation to protect your children's financial wellbeing.
            </p>
            <p className="text-lg text-muted-foreground">
              Texas has specific guidelines for calculating child support based on the paying parent's net income and number of children. We ensure support orders are fair, accurate, and properly enforced.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">How Child Support Is Calculated in Texas</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg mb-6">
              <Calculator className="h-10 w-10 text-accent mb-4" />
              <h3 className="text-xl font-bold mb-3">Texas Child Support Guidelines</h3>
              <p className="text-muted-foreground mb-4">
                Texas uses percentage-based guidelines based on the non-custodial parent's net monthly resources (after taxes and certain deductions):
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-card p-4 rounded">
                <div>
                  <p className="text-sm mb-2"><strong>Number of Children:</strong></p>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• 1 child: <strong>20%</strong> of net income</li>
                    <li>• 2 children: <strong>25%</strong> of net income</li>
                    <li>• 3 children: <strong>30%</strong> of net income</li>
                    <li>• 4 children: <strong>35%</strong> of net income</li>
                    <li>• 5+ children: <strong>40%</strong> of net income</li>
                  </ul>
                </div>
                <div>
                  <p className="text-sm mb-2"><strong>Example:</strong></p>
                  <p className="text-sm text-muted-foreground">
                    If a parent earns $5,000/month net and has 2 children, the guideline support is:
                  </p>
                  <p className="text-sm font-bold text-accent mt-2">
                    $5,000 × 25% = $1,250/month
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
              <h3 className="text-lg font-bold mb-3">Cap on High Incomes</h3>
              <p className="text-muted-foreground mb-2">
                For monthly net resources over $9,200, the court has discretion. The guideline percentage applies to the first $9,200, and additional support may be ordered based on the child's needs.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Example:</strong> If net income is $15,000/month for 1 child: $9,200 × 20% = $1,840 (minimum), plus additional amount at court's discretion.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">What's Included in "Net Resources"?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-accent">Included Income:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Wages and salary</li>
                  <li>• Bonuses and commissions</li>
                  <li>• Self-employment income</li>
                  <li>• Rental income</li>
                  <li>• Interest and dividends</li>
                  <li>• Unemployment benefits</li>
                  <li>• Workers' compensation</li>
                  <li>• Social Security retirement/disability</li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold mb-3 text-destructive">Deductions Allowed:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Social Security taxes</li>
                  <li>• Federal income tax (claiming 1 exemption)</li>
                  <li>• State income tax</li>
                  <li>• Union dues</li>
                  <li>• Health insurance premiums for the child</li>
                  <li>• Other children's support actually paid</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Child Support Modification</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg">
              <TrendingUp className="h-10 w-10 text-accent mb-4" />
              <p className="text-muted-foreground mb-4">
                Child support can be modified if circumstances have materially and substantially changed since the last order, or if it's been at least 3 years since the order and the guideline amount differs by 20% or $100 from the current order.
              </p>
              <p className="font-bold mb-2">Common Reasons for Modification:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <p className="text-sm text-muted-foreground">• Significant income change (job loss or raise)</p>
                <p className="text-sm text-muted-foreground">• Change in child's needs</p>
                <p className="text-sm text-muted-foreground">• Change in medical insurance costs</p>
                <p className="text-sm text-muted-foreground">• Birth of additional children</p>
                <p className="text-sm text-muted-foreground">• Change in custody arrangement</p>
                <p className="text-sm text-muted-foreground">• Child turns 18 or graduates high school</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Enforcement of Child Support</h2>
            <div className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg">
              <AlertCircle className="h-10 w-10 text-destructive mb-4" />
              <p className="text-muted-foreground mb-4">
                If a parent fails to pay court-ordered child support, Texas has powerful enforcement mechanisms:
              </p>
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <span className="text-destructive font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Wage Garnishment</p>
                    <p className="text-xs text-muted-foreground">Automatic withholding from paychecks</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-destructive font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Tax Refund Intercept</p>
                    <p className="text-xs text-muted-foreground">Federal and state tax refunds seized</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-destructive font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">License Suspension</p>
                    <p className="text-xs text-muted-foreground">Driver's license, professional licenses, hunting/fishing licenses</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-destructive font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Contempt of Court</p>
                    <p className="text-xs text-muted-foreground">Jail time for willful failure to pay</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-destructive font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Credit Reporting</p>
                    <p className="text-xs text-muted-foreground">Delinquency reported to credit bureaus</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <span className="text-destructive font-bold">•</span>
                  <div>
                    <p className="font-bold text-sm">Liens on Property</p>
                    <p className="text-xs text-muted-foreground">Liens placed on real estate and personal property</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Additional Support Obligations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <FileText className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Medical Support</h3>
                <p className="text-sm text-muted-foreground">
                  Parents must provide health insurance for the child if available through employment at reasonable cost. Uninsured medical expenses are typically split proportionally based on income.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg shadow-md border-l-4 border-accent">
                <FileText className="h-8 w-8 text-accent mb-3" />
                <h3 className="text-xl font-bold mb-2">Dental & Vision</h3>
                <p className="text-sm text-muted-foreground">
                  Courts can order dental and vision insurance if available at reasonable cost, or require parents to split unreimbursed dental/vision expenses.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">When Does Child Support End?</h2>
            <p className="text-muted-foreground mb-4">
              Child support in Texas typically ends when the child:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Turns 18 AND graduates high school, OR</li>
              <li>• Turns 19 (even if still in high school)</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              <strong>Exception:</strong> Support can continue indefinitely if the child is disabled and unable to support themselves.
            </p>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Need Help with Child Support?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Whether you need to establish, modify, or enforce child support, contact us today for experienced legal representation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default ChildSupport;
